import { useCallback } from 'react';
import { notificationApi } from '../notificationsClient';
import { settingsActions } from '../settingsStore';
import type { NotificationRule } from '../types';

export interface RuleDraft {
  id?: string;
  name: string;
  keywords: readonly string[];
  urgent?: boolean;
  enabled?: boolean;
}

/**
 * Keyword alerts, persisted.
 *
 * The editor used to write straight to the in-memory store: every rule
 * evaporated on reload, and because nothing server-side ever read
 * `cyphex.notification_rules`, the promise on the form — "pierces quiet hours
 * and Do not disturb" — could not be kept by anything. `POST/DELETE
 * /notifications/rules` now round-trip, and services/notifyDelivery.ts
 * evaluates them when it decides whether a notification may interrupt.
 */
export function useRuleActions(): {
  save: (draft: RuleDraft) => void;
  remove: (id: string) => void;
  toggle: (rule: NotificationRule, enabled: boolean) => void;
} {
  const save = useCallback((draft: RuleDraft) => {
    void notificationApi
      .saveRule(draft)
      .then(settingsActions.upsertRule)
      .catch(() => undefined);
  }, []);

  return {
    save,
    remove: useCallback((id: string) => {
      settingsActions.removeRule(id);
      void notificationApi.deleteRule(id).catch(() => undefined);
    }, []),
    toggle: useCallback(
      (rule: NotificationRule, enabled: boolean) => {
        save({
          id: rule.id,
          name: rule.name,
          keywords: rule.match.keywords ?? [],
          urgent: rule.action.priority === 'urgent',
          enabled,
        });
      },
      [save],
    ),
  };
}
